import type { FrameworkPackDefinition } from "@/lib/behavior/frameworkPacks/frameworkPackTypes";

export const AUTHENTIC_LEADERSHIP_PACK: FrameworkPackDefinition = {
  id: "authentic_leadership",
  label: "Authentic Leadership",
  family: "coach_custom",
  source: "primary_source",
  preview: {
    philosophySummary:
      "Authentic Leadership treats pressure as a crucible that reveals values, identity, trust, and the need for accountable growth.",
    whatItNotices: [
      "Whether a crucible is producing avoidance or honest self-examination.",
      "Where trust, integrity, compassion, and accountability reinforce or contradict each other.",
      "Whether the leader is turning hard moments into learning and micro-moments of meaning.",
    ],
    watchouts: [
      "Do not romanticize suffering or confuse struggle with growth.",
      "Do not treat authenticity as self-expression without responsibility.",
      "Do not let reflection replace accountable behavior change.",
    ],
    sampleCoachLanguage: [
      "What is this crucible asking you to become more honest about?",
      "Where is your behavior aligned with your values, and where is it not?",
    ],
    sampleLeaderLanguage: [
      "Your coach would likely want you to notice what this moment reveals about your values under pressure.",
      "Through this lens, trust grows when your behavior becomes more consistent, not just more self-aware.",
    ],
  },
  runtime: {
    coreConstructs: [
      "Crucible",
      "Self-awareness",
      "Integrity",
      "Compassion",
      "Relatableness",
      "Learning",
      "Trust",
      "Accountability",
      "Micro-moments of meaning",
    ],
    diagnosticQuestions: [
      "What is the crucible in this leader's current season, and what truth is it surfacing?",
      "Where do stated values and observable behavior align or drift apart?",
      "What would accountable authenticity look like in the next real interaction?",
    ],
    interventionPatterns: [
      "Name the crucible before solving it.",
      "Link self-awareness to one accountable behavior change.",
      "Turn a hard moment into one meaningful practice rep.",
    ],
    progressMarkers: [
      "The leader can name what the crucible is teaching them without dramatizing it.",
      "Integrity, trust, and accountability show up together in observable behavior.",
      "Reflection is turning into one meaningful behavior rep in the next hard moment.",
    ],
    stallSignals: [
      "The leader uses reflection to avoid responsibility or a concrete next step.",
      "Authenticity is framed as self-expression without accountability to others.",
      "The crucible is being romanticized instead of translated into learning and behavior change.",
    ],
  },
  operationalization: {
    noticingSignals: [
      {
        id: "values_under_pressure",
        label: "Values under pressure",
        description: "A pressure moment reveals possible alignment or drift between stated values and observable behavior.",
        positiveIndicators: ["values", "pressure", "integrity", "accountability", "trust"],
        negativeIndicators: ["drift", "gap", "inconsistent", "hard moment"],
        evidenceHints: ["values under pressure", "integrity gap", "trust consistency"],
        prohibitedInferences: ["identity conclusion", "character judgment", "motive claim"],
      },
      {
        id: "reflection_without_accountability",
        label: "Reflection without accountability",
        description: "Reflection is present but may not yet connect to an accountable behavior shift.",
        positiveIndicators: ["reflection", "learning", "aware", "crucible"],
        negativeIndicators: ["no next step", "not accountable", "self-expression", "stuck"],
        evidenceHints: ["reflection replacing action", "accountable authenticity", "behavior alignment"],
        prohibitedInferences: ["romanticized suffering", "inauthentic person", "low integrity"],
      },
    ],
    reflectionPatterns: [
      {
        id: "accountable_authenticity",
        label: "Accountable authenticity",
        promptTemplates: [
          "What might this pressure moment reveal about values in action?",
          "Where did behavior align with stated values, and where was there drift?",
          "What would accountable authenticity look like in the next real interaction?",
        ],
        useWhen: ["values_under_pressure", "reflection_without_accountability"],
        avoidWhen: ["single_identity_claim", "thin_evidence"],
      },
    ],
    boundaries: {
      allowed: ["values under pressure", "trust consistency", "integrity-accountability gap", "crucible moment"],
      prohibited: ["romanticize suffering", "identity conclusion", "inauthentic person", "low integrity"],
      coachOnly: ["interpret the leader's crucible", "confront integrity gaps", "design accountable practice reps"],
    },
    salienceWeights: {
      values_under_pressure: 1.5,
      reflection_without_accountability: 1.4,
    },
  },
};
